import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import AdminLayout from './components/layout/AdminLayout';
import ProtectedRoute from './components/layout/ProtectedRoute';
import LoadingScreen from './components/ui/LoadingScreen';

// ── Lazy-loaded Pages ─────────────────────────────────────────
// Admin Pages
const AdminDashboard    = lazy(() => import('./pages/admin/Dashboard'));
const ElectionList      = lazy(() => import('./pages/admin/ElectionList'));
const CreateElection    = lazy(() => import('./pages/admin/CreateElection'));
const EditElection      = lazy(() => import('./pages/admin/EditElection'));
const ElectionDetail    = lazy(() => import('./pages/admin/ElectionDetail'));
const ManageBallot      = lazy(() => import('./pages/admin/ManageBallot'));
const ManageVoters      = lazy(() => import('./pages/admin/ManageVoters'));
const AdminResults      = lazy(() => import('./pages/admin/AdminResults'));
const ManageCandidates     = lazy(() => import('./pages/admin/ManageCandidates'));
const ManageQuestionnaire  = lazy(() => import('./pages/admin/ManageQuestionnaire'));
const QuestionnaireResults = lazy(() => import('./pages/admin/QuestionnaireResults'));
const AuditLog             = lazy(() => import('./pages/admin/AuditLog'));
const VoterMap              = lazy(() => import('./pages/admin/VoterMap'));
const WinnerDeclaration     = lazy(() => import('./pages/admin/WinnerDeclaration'));
const AdminLogin        = lazy(() => import('./pages/admin/AdminLogin'));

// Voter Pages
const VoterLogin        = lazy(() => import('./pages/voter/VoterLogin'));
const BallotPage        = lazy(() => import('./pages/voter/BallotPage'));
const ThankYouPage      = lazy(() => import('./pages/voter/ThankYouPage'));
const AlreadyVotedPage  = lazy(() => import('./pages/voter/AlreadyVotedPage'));

// Public Pages
const PublicResult      = lazy(() => import('./pages/public/PublicResult'));
const NotFound          = lazy(() => import('./pages/NotFound'));

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            {/* ── Root redirect ── */}
            <Route path="/" element={<Navigate to="/voter/login" replace />} />

            {/* ── Admin Auth ── */}
            <Route path="/admin/login" element={<AdminLogin />} />

            {/* ── Admin Protected Routes ── */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute role="admin">
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard"                    element={<AdminDashboard />} />
              <Route path="elections"                    element={<ElectionList />} />
              <Route path="elections/create"             element={<CreateElection />} />
              <Route path="elections/:id"                element={<ElectionDetail />} />
              <Route path="elections/:id/edit"           element={<EditElection />} />
              <Route path="elections/:id/ballot"         element={<ManageBallot />} />
              <Route path="elections/:id/voters"         element={<ManageVoters />} />
              <Route path="elections/:id/results"        element={<AdminResults />} />
              <Route path="elections/:id/positions/:positionId/candidates" element={<ManageCandidates />} />
              <Route path="elections/:id/questionnaire"         element={<ManageQuestionnaire />} />
              <Route path="elections/:id/questionnaire/results" element={<QuestionnaireResults />} />
              <Route path="audit-log" element={<AuditLog />} />
              <Route path="voter-map"  element={<VoterMap />} />
              <Route path="elections/:id/winners" element={<WinnerDeclaration />} />
            </Route>

            {/* ── Voter Routes ── */}
            <Route path="/voter/login"        element={<VoterLogin />} />
            <Route
              path="/voter/ballot/:electionId"
              element={
                <ProtectedRoute role="voter">
                  <BallotPage />
                </ProtectedRoute>
              }
            />
            <Route path="/voter/thank-you"    element={<ThankYouPage />} />
            <Route path="/voter/already-voted" element={<AlreadyVotedPage />} />

            {/* ── Public Routes ── */}
            <Route path="/public/:token/results" element={<PublicResult />} />

            {/* ── 404 ── */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>

        {/* Global Toast Notification */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#1E3A5F',
              color: '#fff',
              borderRadius: '12px',
              fontSize: '14px',
              padding: '12px 16px',
            },
            success: {
              iconTheme: { primary: '#22C55E', secondary: '#fff' },
            },
            error: {
              style: { background: '#DC2626' },
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}
